import { THEME, ThemeProvider } from '@vodacomsa/ns-react-components';
import {
  Form,
  Formik
} from 'formik';
import React, {
  useEffect,
  useMemo
} from 'react';
import { useLocation, useOutletContext, useNavigate } from 'react-router-dom';
import {
  useRecoilState,
  useRecoilValue
} from 'recoil';

import {formatPayload} from '../../../helpers';
import { FormSubmitHelper, SupportPopup, ErrorModalSection } from '../../../components';
import { ApplicationRoutes } from '../../../Router';
import { PersonalDetailsSchema } from './PersonalDetailsSchema';
import { useModal } from '../../../components/modalController/useModal';
import {
  FormContainer,
  FormField,
  personalDetailsKeys
} from '../../../constants';
import {
  mapValues,
  stripEmpty
} from '../../../helpers';
import { findFirstValidationError } from '../../../helpers/formik';
import {
  personalDetailsSubmit,
  useIsMobile,
  applicationsList,
  businessList
} from '../../../recoil';
import { useTealium } from '../../../tealium/useTealium';
import { setPersonalDetails } from '../../../api/vodaLend';
import { StatusCodes } from 'http-status-codes';
import styles from './PersonalDetails.module.css';
import { FormBuilder } from '../../../components/FormBuilder';
import { getFormFields } from './PersonalDetailsConstants';
import { useLoader } from '../../../components/Loader/useLoader';
import { StaticRoutes } from '../../../Router';

export const PersonalDetails = () => {
  const [details, setDetails] = useRecoilState(personalDetailsSubmit);
  const _businessList = useRecoilValue(businessList);
  const [openModal, closeModal] = useModal();
  const [openLoader, closeLoader] = useLoader();
  const { submitState, setSubmitState, backClick, submissionStates, nextClick } = useOutletContext();
  const appsList = useRecoilValue(applicationsList);
  const navigate = useNavigate();

  const formRef = React.useRef();
  const location = useLocation();

  const extractAddressField = (address = {}, field) => address?.[field] || '';

  const buildFormDetails = (details = {}) => {
    const address = details.address || {};
    const unitNumber = extractAddressField(address, 'unitNumber');
    const building = extractAddressField(address, 'building');
    const street = extractAddressField(address, 'street');
    const suburb = extractAddressField(address, 'suburb');
    const city = extractAddressField(address, 'city');
    const postalCode = extractAddressField(address, 'postalCode');
    const province = extractAddressField(address, 'province');

    return {
      passportNumber: details?.idNumber || '',
      title: details?.title || '',
      positionInCompany: details?.positionInCompany || '',
      residentialStatus: details?.residentialStatus || '',
      idType: details?.idType || 'id',
      idNumber: details?.idNumber || '',
      passportCountry: details.idType === 'sapp' ? 'ZA' : details?.passportCountry || '',
      whenMovedIn: details?.whenMovedIn || '',
      gender: details?.gender,
      dateOfBirth: details?.dateOfBirth,
      postalCode: postalCode,
      provinces: province,
      country: 'ZA',
      suburb: suburb,
      city: city,
      buildingType: details?.address?.buildingType || '',

      houseUnitNumber: unitNumber,
      flatUnitNumber: unitNumber,
      townUnitNumber: unitNumber,
      officeUnitNumber: unitNumber,

      flatBuilding: building,
      townBuilding: building,
      officeBuilding: building,

      houseStreet: street,
      flatStreet: street,
      townStreet: street,
      officeStreet: street
    };
  };

  const formDetails = useMemo(() => (details ? buildFormDetails(details) : {}), [details]);
  const { track, events } = useTealium();
  let formTouched = false;

  const handleFormFocus = () => {
    if (formTouched) {
      return;
    }

    formTouched = true;
    track(events.personalDetails.formStart());
  };
  const isMobile = useRecoilValue(useIsMobile);

  const isDetailsCaptured = (details, personalDetailsKeys) => {
    return personalDetailsKeys.every(key => details[key] !== null && details[key] !== undefined && details[key] !== '');
  };
  const closeErrorModal = () => {
    setSubmitState(submissionStates.default);
    closeModal();
  };

  const formatFormData = (formData) => mapValues(stripEmpty(formData));

  const formSubmit = async (formValues) => {
    track(events.personalDetails.formEnd('next'));
    const formatedFormData = formatFormData(formValues);
    let personalDetailResponse;
    openLoader();
    const formatedPayloadData = formatPayload(formatedFormData);
    personalDetailResponse = await setPersonalDetails({...details, ...formatedPayloadData});
    closeLoader();
    if ([StatusCodes.CREATED, StatusCodes.OK].includes(personalDetailResponse.status)) {
      localStorage.setItem('loanApplicationStepKey', 'businessDetails');
      setDetails(personalDetailResponse.data);
      if (_businessList?.businesses?.length === 0) {
        window.location.pathname = ApplicationRoutes.businessVerification;
      } else {
        window.location.pathname = ApplicationRoutes.businessList;
      }
      setSubmitState(submissionStates.submitSuccess);
      nextClick();
    } else if (personalDetailResponse.data.error) {
      openModal({
        title: 'Error',
        children: <ErrorModalSection
          traceId={personalDetailResponse?.traceId}
          error={personalDetailResponse?.error}
          onCloseErrorModal={closeErrorModal}
        />,
        type: 'error',
        onCloseModal: closeErrorModal
      });
      setSubmitState(submissionStates.submitError);
    }
    return personalDetailResponse;
  };

  useEffect(() => {
    track(events.personalDetails.pageView());
  }, []);

  const handleFormikTouchedErrors = (touched, errors) => {
    const errorMessage = findFirstValidationError(errors, touched);

    if (!errorMessage) {
      return;
    }
    track(events.error('form', location.pathname, errorMessage));
  };

  const handleBackClick = () => {
    track(events.personalDetails.formEnd('back'));
    if (!Array.isArray(appsList) || appsList.length === 0) {
      backClick();
    } else {
      navigate(StaticRoutes.FundingApplicationDetails);
    }
  };

  return (
    <div className={styles.container}>

      <ThemeProvider theme={THEME.revamp}>
        <Formik
          initialValues={formDetails}
          onSubmit={formSubmit}
          validate={(values) => {
            try {
              PersonalDetailsSchema.validateSync(values, { abortEarly: false });
              return {};
            } catch (error) {
              return error.inner.reduce((acc, err) => {
                acc[err.path] = err.message;
                return acc;
              }, {});
            }
          }}
          validateOnBlur={true}
          validateOnMount={true}
          innerRef={formRef}
        >
          {({ values, errors, touched }) => {

            handleFormikTouchedErrors(touched, errors);
            return (
              <Form style={FormContainer} onFocus={handleFormFocus}>
                <FormSubmitHelper submitState={submitState} setSubmitState={setSubmitState} />

                <FormBuilder
                  fields={getFormFields(details)}
                  title="Approved business representative details"
                  subtitle={!isDetailsCaptured(formDetails, personalDetailsKeys) ? 'Please provide approved business representative information.' : 'Please confirm your personal details as the approved business representative'}
                  isMobile={isMobile}
                  values={values}
                  FormField={FormField}
                  errors={errors}
                  submitState={submitState}
                  setSubmitState={setSubmitState}
                  backClick={handleBackClick}
                  isDetailsCaptured={isDetailsCaptured(formDetails, personalDetailsKeys)}
                  submissionStates={submissionStates}
                  nextClick={nextClick}
                  touched={touched}
                />

              </Form>
            );
          }}
        </Formik>
      </ThemeProvider>
      <SupportPopup />
    </div>
  );
};
